'use client';

import { useId, useState } from 'react';

import { useRouter } from 'next/navigation';

import { AuthLoading } from '@/components/auth/auth-loading';
import FacebookLogo from '@/components/icons/Facebook';
import GitHubLogo from '@/components/icons/GitHub';
import GoogleLogo from '@/components/icons/Google';
import Button from '@/components/ui/Button/Button';
import { useToast } from '@/components/ui/Toasts/use-toast';
import { Input } from '@/components/ui/input';

import { AlertCircle, CheckCircle, Eye, EyeOff, Mail } from 'lucide-react';
import { signIn } from 'next-auth/react';
import posthog from 'posthog-js';

interface SignInFormProps {
  redirectTo?: string;
}

export function SignInForm({ redirectTo }: SignInFormProps) {
  const router = useRouter();

  // Determine the default redirect URL
  // Determine the default redirect URL
  const defaultRedirect = '/blog';
  const finalRedirect = redirectTo || defaultRedirect;

  const { toast } = useToast();
  const emailId = useId();
  const passwordId = useId();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await signIn('credentials', {
        email,
        password,
        redirect: false
      });
      if (res?.error) throw new Error(res.error);
      toast.success('Welcome back!', {
        description: 'You have been successfully signed in.'
      });
      router.push(finalRedirect);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      toast.error('Sign in failed', {
        description: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error('Email required', {
        description: 'Please enter your email address first.'
      });
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await signIn('resend', { email, redirect: false });
      if (res?.error) throw new Error(res.error);
      posthog.capture('magic_link_requested', { auth_method: 'email' });
      setMagicLinkSent(true);
      toast.success('Magic link sent!', {
        description: 'Check your email for the sign-in link.'
      });
    } catch (err) {
      posthog.captureException(err);
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      toast.error('Failed to send magic link', {
        description: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProviderSignIn = async (
    provider: 'google' | 'github' | 'facebook'
  ) => {
    setLoading(true);
    setError('');

    try {
      await signIn(provider, { callbackUrl: finalRedirect });
      toast.info('Redirecting...', {
        description: `Signing in with ${provider.charAt(0).toUpperCase() + provider.slice(1)}`
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'OAuth sign-in failed';
      setError(errorMessage);
      toast.error('Sign-in failed', {
        description: 'Unable to complete social sign-in.'
      });
      setLoading(false);
    }
  };

  if (magicLinkSent) {
    return (
      <div className='space-y-4'>
        <div className='rounded-md border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20'>
          <div className='flex items-center'>
            <CheckCircle className='mr-2 size-5 text-green-600 dark:text-green-400' />
            <div>
              <p className='text-sm font-medium text-green-800 dark:text-green-200'>
                Magic link sent!
              </p>
              <p className='mt-1 text-xs text-green-700 dark:text-green-300'>
                Check your email for a secure sign-in link.
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => setMagicLinkSent(false)}
          variant='outline'
          className='w-full !px-8 !py-4'>
          Back to Sign In
        </Button>
      </div>
    );
  }

  if (loading) {
    return <AuthLoading message='Signing you in...' />;
  }

  return (
    <div className='space-y-6'>
      {error && (
        <div className='rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20'>
          <div className='flex items-center'>
            <AlertCircle className='mr-2 size-5 shrink-0 text-red-600 dark:text-red-400' />
            <p className='text-sm text-red-800 dark:text-red-200'>{error}</p>
          </div>
        </div>
      )}

      {/* Social Sign In */}
      <div className='space-y-3'>
        <Button
          onClick={() => handleProviderSignIn('google')}
          disabled={loading}
          variant='outline'
          className='w-full !rounded-2xl !px-8 !py-5 text-[14px] font-bold tracking-tight'>
          <GoogleLogo
            size={18}
            className='mr-2'
          />
          {loading ? 'Connecting...' : 'Sign in with Google'}
        </Button>

        <Button
          onClick={() => handleProviderSignIn('github')}
          disabled={loading}
          variant='outline'
          className='w-full !rounded-2xl !px-8 !py-5 text-[14px] font-bold tracking-tight'>
          <GitHubLogo
            size={18}
            className='mr-2'
          />
          {loading ? 'Connecting...' : 'Sign in with GitHub'}
        </Button>

        <Button
          onClick={() => handleProviderSignIn('facebook')}
          disabled={loading}
          variant='outline'
          className='w-full !rounded-2xl !px-8 !py-5 text-[14px] font-bold tracking-tight'>
          <FacebookLogo
            width={18}
            height={18}
            className='mr-2'
          />
          {loading ? 'Connecting...' : 'Sign in with Facebook'}
        </Button>
      </div>

      <div className='relative flex items-center py-2'>
        <div className='border-muted/80 flex-grow border-t'></div>
        <span className='text-muted-foreground shrink-0 px-4 text-xs font-medium uppercase'>
          Or continue with email
        </span>
        <div className='border-muted/80 flex-grow border-t'></div>
      </div>

      {/* Email Sign In Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleMagicLink();
        }}
        className='space-y-4'>
        <div className='space-y-2'>
          <label
            htmlFor={emailId}
            className='text-sm font-medium'>
            Email Address
          </label>
          <Input
            id={emailId}
            type='email'
            placeholder='name@example.com'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            autoComplete='email'
            className='!py-6 !rounded-2xl !px-8 text-[15px]'
          />
        </div>

        <Button
          type='submit'
          variant='primary'
          className='w-full font-bold !rounded-2xl !py-6 !text-[15px] tracking-tight'
          disabled={loading}>
          {loading ? 'Sending link...' : 'Sign In with Email'}
        </Button>
      </form>

    </div>
  );
}
